import { SprinklesElement } from "sprinkles-js";

const FOCUS_OPTIONS = {
  input: "INPUT",
  listbox: "LISTBOX",
};

const ANDROID_KEYS = [
  "Process", // Firefox
  "Unidentified", // Chrome (but only 127+?)
];

// NOTE: Certain android devices always send an event with a special key and/or keyCode `229`
function isAndroidKey(event) {
  return ANDROID_KEYS.includes(event.key) || event.keyCode === 229;
}

function isPrintableCharacter(str) {
  return str.length === 1 && str.match(/\S| /);
}

function isOptionInView(element) {
  const { top, left, bottom, right } = element.getBoundingClientRect();
  return (
    top >= 0 &&
    left >= 0 &&
    bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    right <= (window.innerWidth || document.documentElement.clientWidth)
  );
}

function normalizeText(text) {
  return text.trim().toLowerCase().normalize("NFKC");
}

export default class ComboBoxElement extends SprinklesElement {
  static tagName = "combo-box";
  static refs = ["input", "listbox", "valueField"];
  static events = {
    click: [
      { method: "handleClick", ref: "input" },
      { method: "handleListboxClick", ref: "listbox" },
    ],
    focus: [{ method: "handleFocus", ref: "input" }],
    blur: [{ method: "handleBlur", ref: "input" }],
    keyup: [{ method: "handleKeyup", ref: "input" }],
    keydown: [{ method: "handleKeydown", ref: "input" }],
    pointerover: { method: "handleListboxPointerover", ref: "listbox" },
    pointerout: { method: "handleListboxPointerout", ref: "listbox" },
    pointerup: { method: "handleBackgroundPointerup", element: document },
  };

  #focusedElement = null;
  #filterValue = "";
  #currentOption = null;
  #selectedOption = null;
  #options = [];
  filteredOptions = [];

  afterConnected() {
    this.#options = [...this.querySelectorAll("[role=option]")];
    this.#selectedOption = this.#options.find(
      (n) => n.getAttribute("aria-selected") === "true",
    );
    this.#filterValue = this.refs.input.value;
  }

  get open() {
    return !this.refs.listbox.hidden;
  }

  // Public getters/setters for private fields
  get focusedElement() {
    return this.#focusedElement;
  }

  set focusedElement(value) {
    this.#focusedElement = value;

    if (value === FOCUS_OPTIONS.input) {
      this.refs.input.setAttribute("data-focus", true);
      this.#setActiveDescendant(false);
    } else {
      this.refs.listbox.removeAttribute("data-focus");
    }

    if (value === FOCUS_OPTIONS.listbox) {
      this.refs.listbox.setAttribute("data-focus", true);
      this.#setActiveDescendant(this.selectedOption);
    } else {
      this.refs.input.removeAttribute("data-focus");
    }
  }

  get filterValue() {
    return this.#filterValue;
  }

  set filterValue(value) {
    this.#filterValue = value;
    this.#filterOptions();
  }

  get selectedOption() {
    return this.#selectedOption;
  }

  set selectedOption(node) {
    if (this.#selectedOption) {
      this.#removeSelectedOptionStyle(this.#selectedOption);
    }
    if (this.#selectedOption === node) {
      this.#selectedOption = null;
    } else {
      this.#selectedOption = node;
      this.#setSelectedOptionStyle(node);
    }
  }

  get currentOption() {
    return this.#currentOption;
  }

  set currentOption(node) {
    this.#currentOption = node;

    this.#setCurrentOptionStyle(node);
    this.#setActiveDescendant(node);
  }

  get value() {
    return this.refs.valueField
      ? this.refs.valueField.value
      : this.refs.input.value;
  }

  set value(newValue) {
    const option = this.#options.find((o) => o.dataset.value === newValue);
    if (option) {
      this.#commitOption(option);
    } else {
      this.#commitValue(newValue, undefined);
    }
  }

  // Next/previous options
  get nextOption() {
    const currentIndex = this.filteredOptions.findIndex(
      (el) => el === this.currentOption,
    );
    return this.filteredOptions.at(
      (currentIndex + 1) % this.filteredOptions.length,
    );
  }

  get previousOption() {
    const currentIndex = this.filteredOptions.findIndex(
      (el) => el === this.currentOption,
    );
    return this.filteredOptions.at(currentIndex - 1);
  }

  // Event handlers and related methods
  handleClick() {
    this.open ? this.hideListbox(true) : this.revealListbox();
  }

  hideListbox(force = false) {
    if (
      force ||
      (!Object.values(FOCUS_OPTIONS).includes(this.focusedElement) &&
        !this.hasHover)
    ) {
      this.#setCurrentOptionStyle(false);
      this.refs.listbox.hidden = true;
      this.refs.input.setAttribute("aria-expanded", "false");
      this.#setActiveDescendant(null);

      this.#commitOption(this.selectedOption);
    }
  }

  revealListbox() {
    this.refs.listbox.hidden = false;
    this.refs.input.setAttribute("aria-expanded", "true");
  }

  handleFocus() {
    this.#filterValue = this.refs.input.value;
    this.focusedElement = FOCUS_OPTIONS.input;
    this.currentOption = null;
    this.#setCurrentOptionStyle(null);
  }

  handleBlur() {
    if (!this.contains(document.activeElement)) this.focusedElement = null;
  }

  handleBackgroundPointerup(event) {
    if (this.contains(event.target)) return;

    this.#setCurrentOptionStyle(null);
    this.focusedElement = null;
    setTimeout(this.hideListbox.bind(this, true), 300);
  }

  handleListboxPointerover() {
    this.hasHover = true;
  }

  handleListboxPointerout() {
    this.hasHover = false;
    setTimeout(this.hideListbox.bind(this, false), 300);
  }

  handleListboxClick(event) {
    const option = event.target.closest('[role="option"]');
    if (!option) return;
    this.selectedOption = option;
    this.hideListbox(true);
  }

  handleOptionPointerover(event) {
    this.currentOption = event.target;
  }

  handleKeydown(event) {
    if (event.ctrlKey || event.shiftKey) return;

    let prevent = false;
    switch (event.key) {
      case "Enter":
        if (!this.open) {
          // Just let the default event for enter continue
          return;
        } else {
          this.selectedOption = this.currentOption;
          this.hideListbox(true);
          this.focusedElement = FOCUS_OPTIONS.input;
          prevent = true;
        }
        break;
      case "Down":
      case "ArrowDown":
        if (this.filteredOptions.length > 0) {
          this.revealListbox();
          if (!event.altKey) {
            this.currentOption = this.nextOption;
            this.focusedElement === FOCUS_OPTIONS.listbox;
          }
        }
        prevent = true;
        break;
      case "Up":
      case "ArrowUp":
        if (this.filteredOptions.length > 0) {
          if (this.focusedElement === FOCUS_OPTIONS.listbox) {
            this.currentOption = this.previousOption;
          } else {
            this.revealListbox();

            if (!event.altKey) {
              this.currentOption = this.previousOption;
              this.focusedElement = FOCUS_OPTIONS.listbox;
            }
          }
        }
        prevent = true;
        break;
      case "Esc":
      case "Escape":
        if (this.open) {
          this.hideListbox(true);
          this.filterValue = this.refs.input.value;
          this.focusedElement = FOCUS_OPTIONS.input;
        } else {
          this.#setInputValue();
        }
        prevent = true;
        break;
      case "Tab":
        this.hideListbox(true);
        if (
          this.focusedElement === FOCUS_OPTIONS.listbox &&
          this.currentOption
        ) {
          this.selectedOption = this.currentOption;
        }
        break;

      case "Home":
        this.refs.input.setSelectionRange(0, 0);
        prevent = true;
        break;

      case "End":
        var length = this.refs.input.value.length;
        this.refs.input.setSelectionRange(length, length);
        prevent = true;
        break;

      default:
        break;
    }

    if (prevent) {
      event.stopPropagation();
      event.preventDefault();
    }
  }
  handleKeyup(event) {
    if (isPrintableCharacter(event.key)) this.filterValue += event.key;
    if (isAndroidKey(event)) this.filterValue = this.refs.input.value;

    if (this.refs.input.value.length < this.filterValue.length) {
      this.currentOption = null;
    }

    if (event.key === "Escape" || event.key === "Esc") {
      return;
    }
    let prevent = false;
    switch (event.key) {
      case "Backspace":
        this.focusedElement = FOCUS_OPTIONS.input;
        this.#setCurrentOptionStyle(false);
        this.filterValue = this.refs.input.value;
        this.currentOption = null;
        prevent = true;
        break;
      case "Left":
      case "ArrowLeft":
      case "Right":
      case "ArrowRight":
      case "Home":
      case "End":
        this.option = null;
        this.#setCurrentOptionStyle(false);
        this.focusedElement = FOCUS_OPTIONS.input;
        prevent = true;
        break;
      default:
        if (!isPrintableCharacter(event.key) && !isAndroidKey(event)) break;

        this.focusedElement = FOCUS_OPTIONS.listbox;
        this.#setCurrentOptionStyle(false);
        prevent = true;

        this.currentOption = this.filteredOptions.at(0);

        if (this.currentOption) {
          if (!this.open && this.refs.input.value.length) {
            this.revealListbox();
          }
          this.#setCurrentOptionStyle(this.currentOption);
        } else {
          this.hideListbox();
          this.option = null;
          this.#setActiveDescendant(false);
        }
    }

    if (prevent) {
      event.stopPropagation();
      event.preventDefault();
    }
  }

  #setActiveDescendant(element) {
    if (element && this.focusedElement === FOCUS_OPTIONS.listbox) {
      this.refs.input.setAttribute("aria-activedescendant", element.id);
      if (!isOptionInView(element)) {
        element.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    } else {
      this.refs.input.setAttribute("aria-activedescendant", "");
    }
  }

  #setCurrentOptionStyle(currentOption) {
    Object.values(this.refs.listbox.children).forEach((option) => {
      if (option !== currentOption) {
        option.removeAttribute("data-current");
        return;
      }
      option.setAttribute("data-current", "true");
      let optionHeight = option.offsetTop + option.offsetHeight;
      if (
        this.refs.listbox.scrollTop + this.refs.listbox.offsetHeight <
        optionHeight
      ) {
        this.refs.listbox.scrollTop =
          optionHeight - this.refs.listbox.offsetHeight;
      } else if (this.refs.listbox.scrollTop > option.offsetTop + 2) {
        this.refs.listbox.scrollTop = option.offsetTop;
      }
    });
  }

  #removeSelectedOptionStyle(node) {
    if (node === null) return;

    node.removeAttribute("aria-selected");
  }

  #setSelectedOptionStyle(node) {
    if (node === null) return;

    node.setAttribute("aria-selected", "true");
  }

  #filterOptions() {
    const normalizedInput = normalizeText(this.filterValue);
    this.filteredOptions = this.#options.filter((node) =>
      this.#matchesFilter(node, normalizedInput),
    );

    this.refs.listbox.innerHTML = "";
    this.filteredOptions.forEach((el) => this.refs.listbox.appendChild(el));
  }

  #matchesFilter(option, normalizedInput) {
    return normalizeText(option.textContent).includes(normalizedInput);
  }

  #setInputValue(value = "") {
    this.refs.input.value = value;
    this.refs.input.setSelectionRange(value.length, value.length);
  }

  #commitOption(node) {
    if (node === undefined) {
      if (this.refs.valueField) {
        this.#commitValue("", "");
      } else {
        // Free-text mode: typed value is the committed value.
        const value = this.refs.input.value;
        this.#commitValue(value, value);
      }
    } else {
      this.#commitValue(node.dataset.value, node.dataset.label.trim());
    }
  }

  #commitValue(value, queryValue) {
    if (this.refs.valueField) {
      if (this.refs.valueField.value === value) return;
      this.refs.valueField.value = value;
      this.#setInputValue(queryValue ?? "");
    } else {
      if (this.refs.input.value === value) return;
      this.#setInputValue(value);
    }
    this.#dispatchChange(value, queryValue);
  }

  #dispatchChange(value, queryValue) {
    this.dispatchEvent(new Event("change", { bubbles: true }));
    this.dispatchEvent(
      new CustomEvent("combobox:change", {
        bubbles: true,
        detail: { value, queryValue },
      }),
    );
  }
}
